import { TWidget } from 'widgets/Widget';
import { getContentByUrl } from 'widgets/toolbox/ajax';

/**
 * @param Widget Base widget for extending
 */
function DynamicRecommendationsClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory product
     * @class DynamicRecommendations
     * @augments Widget
     * @classdesc DynamicRecommendations Widget - handles dynamic reloading of Einstein recommendations
     * when product variation (color) changes on PDP
     * @property {string} data-widget - Widget name `dynamicRecommendations`
     * @property {string} data-current-product-id - Current product ID
     * @example <caption>Example of DynamicRecommendations widget usage</caption>
     * <div
     *     data-widget="dynamicRecommendations"
     *     data-current-product-id="${product.id}"
     *     data-ref="recommendationsContainer"
     * >
     *     <!-- Recommendations content here -->
     * </div>
     */
    class DynamicRecommendations extends Widget {
        prefs() {
            return {
                currentProductId: '',
                refRecommendationsContainer: 'recommendationsContainer',
                endpoint: '',
                ...super.prefs()
            };
        }

        init() {
            // Listen for product variation changes from the parent ProductDetail widget
            this.eventBus().on('product.color.change', 'onProductColorChange');
        }

        /**
         * @description Handler for product variation change event
         * @param data - Event data containing the new product information
         */
        onProductColorChange(data: { id: string }) {
            const newProductId = data.id;
            const currentProductId = this.prefs().currentProductId;

            // Only reload if the product ID actually changed
            if (newProductId && newProductId !== currentProductId) {
                this.reloadRecommendations(newProductId);
            }
        }

        /**
         * @description Reload Einstein recommendations for a new product
         * @param productId - The new product ID to load recommendations for
         */
        reloadRecommendations(productId: string) {
            const url = new URL(this.prefs().endpoint);

            url.searchParams.set('pid', productId);

            return getContentByUrl(url.toString())
                .then(response => {
                    // append expects scripts to be with type="text/javascript"
                    response = response.replace(/<script>/g, '<script type="text/javascript">');

                    this.ref('self').empty().append(response);

                    // Update the current product ID
                    this.setPrefs({ currentProductId: productId });
                    this.ref('self').attr('data-current-product-id', productId);
                })
                .catch(error => {
                    // eslint-disable-next-line no-console
                    console.error('Failed to reload recommendations:', error);
                });
        }
    }

    return DynamicRecommendations;
}

export type TDynamicRecommendations = ReturnType<typeof DynamicRecommendationsClassCreator>;

export type TDynamicRecommendationsInstance = InstanceType<TDynamicRecommendations>;

export default DynamicRecommendationsClassCreator;
